import {
  PRIORIDADES, PRIORITY_LABEL, isOverdue, localToday, parseMoney, sortActions, w5h2Metrics,
  type Prioridade, type W5h2Action,
} from '@ads/shared';
import { useEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { brl, dateBR } from '../lib/format';
import { useConsolidate, useW5h2Suggest } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';

/* ─────────────────────────────────────────────────────────────────────
   Plano de Ação (5W2H).
   Na primeira visita, a IA lê o snapshot do Strategy e propõe as ações
   iniciais — prioridade e "o quê", com a fonte de cada uma. A organização
   preenche o resto (por quê, onde, quem, quando, como, quanto), acrescenta
   linhas e edita tudo, inclusive o que a IA propôs. A tabela está sempre
   por prioridade.
   ───────────────────────────────────────────────────────────────────── */

const newId = () => `ac-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const blank = (a: W5h2Action) => !a.what.trim() && !a.why.trim() && !a.where.trim() && !a.who.trim() && !a.when && !a.how.trim() && a.howMuch == null;
/** ⏎ conclui o campo (sem quebrar linha e sem acionar o "Concluir ferramenta"); ⇧⏎ quebra a linha. */
const done = (e: KeyboardEvent<HTMLElement>) => {
  if (e.key !== 'Enter' || e.shiftKey) return;
  e.preventDefault(); e.stopPropagation(); e.currentTarget.blur();
};
const focusRow = (id: string) => requestAnimationFrame(() => {
  const el = document.querySelector<HTMLTextAreaElement>(`[data-ac="${id}"] .pa-what`);
  el?.focus();
  el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
});

export function PlanoAcao() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Plano de Ação'], j: .58 });
  const nav = useNavigate();
  const { local, commit, flush, saveState, status } = useToolDraft('w5h2');
  const suggest = useW5h2Suggest();
  const consolidate = useConsolidate();
  const [note, setNote] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string[]>([]);
  const c = local;
  const cur = useRef(c);
  cur.current = c;
  const started = useRef(false);

  const rows = useMemo(() => (c ? sortActions(c.actions) : []), [c]);
  const m = useMemo(() => (c ? w5h2Metrics(c) : null), [c]);

  /** A IA propõe; as ações entram no fim da sua prioridade, marcadas com a fonte. */
  const generate = (first = false) => {
    if (!cur.current || suggest.isPending) return;
    setNote(null);
    suggest.mutate(first ? [] : cur.current.actions.map(a => a.what).filter(Boolean), {
      onSuccess: d => {
        const now = cur.current;
        if (!now) return;
        const add: W5h2Action[] = d.acoes.map(a => ({
          id: newId(), what: a.what, priority: a.priority, why: '', where: '', who: '', when: '', how: '', howMuch: null, ai: true, origin: a.fonte, doneAt: '',
        }));
        commit({ ...now, actions: [...now.actions, ...add], aiAt: new Date().toISOString() });
        setFresh(add.map(a => a.id!));
        setNote(d.note ?? (add.length ? null : 'Nada novo a propor agora: o plano já cobre o que o Strategy mostrou.'));
      },
      onError: () => setNote('A IA não respondeu agora. Tente de novo em instantes.'),
    });
  };
  /* primeira visita, plano vazio: a IA lê o Strategy e propõe */
  useEffect(() => {
    if (!c || started.current || c.aiAt || c.actions.some(a => a.what.trim())) return;
    started.current = true;
    generate(true);
  });
  useEffect(() => { if (!fresh.length) return; const t = setTimeout(() => setFresh([]), 2600); return () => clearTimeout(t); }, [fresh]);

  const patch = (id: string, p: Partial<W5h2Action>) => {
    if (!c) return;
    /* reescrever o "o quê" faz da ação uma ação da organização (a fonte fica) */
    commit({ ...c, actions: c.actions.map(a => (a.id === id ? { ...a, ...p, ai: p.what !== undefined ? false : a.ai } : a)) });
  };
  const add = () => {
    if (!c) return;
    const id = newId();
    commit({ ...c, actions: [...c.actions.filter(a => !blank(a)), { id, what: '', why: '', where: '', who: '', when: '', how: '', howMuch: null, priority: 'media', ai: false, origin: '', doneAt: '' }] });
    focusRow(id);
  };
  const remove = (id: string) => { if (c) commit({ ...c, actions: c.actions.filter(a => a.id !== id) }); };
  const leave = (id: string) => (e: FocusEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    const a = cur.current?.actions.find(x => x.id === id);
    if (a && blank(a)) remove(id);
  };
  const dropBlank = () => { if (c && c.actions.some(blank)) commit({ ...c, actions: c.actions.filter(a => !blank(a)) }); };
  const leaveTo = (to: string) => { dropBlank(); flush(); nav(to); };
  const finish = () => { dropBlank(); flush(); consolidate.mutate(undefined, { onSettled: () => nav('/dna') }); };
  const thinking = suggest.isPending;

  return (
    <section className="view on" data-v="w5h2">
      <button className="back" type="button" onClick={() => leaveTo('/strategy')}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 10 / 10</b></div>
          <h1>Plano de Ação</h1>
          <p className="lede">Priorize e organize as ações estratégicas no formato 5W2H: o quê, por quê, onde, quem, quando, como e quanto custa.</p>
        </div>
        <div className="tools pa-tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
          <button type="button" className="btn-ghost" onClick={() => generate()} disabled={thinking || !c}
            title="A IA lê o que as telas do Strategy registraram e propõe ações que ainda não estão no plano">
            {thinking ? <span className="spin" /> : <Ic.Sparkle />}Propor ações com IA
          </button>
          <button type="button" className="btn-ghost pa-add" onClick={add} disabled={!c}><Ic.Plus />Adicionar ação</button>
        </div>
      </div>

      {m && (
        <div className="pa-sum" aria-label="Resumo do plano">
          <span className="pa-n"><b>{m.total}</b> {m.total === 1 ? 'ação' : 'ações'}</span>
          {PRIORIDADES.map(p => <span key={p} className={`pa-chip p-${p}`}>{PRIORITY_LABEL[p]} · {m.by[p]}</span>)}
          <span className="pa-sep" />
          <span className="pa-k">Investimento previsto <b>{brl(m.invest)}</b>{m.priced < m.total && <em> · {m.priced} de {m.total} com valor</em>}</span>
          <span className="pa-k">Próxima entrega <b>{m.next ? dateBR(m.next) : '—'}</b></span>
          {m.done > 0 && <span className="pa-k pa-ok"><Ic.Check />{m.done} {m.done === 1 ? 'concluída' : 'concluídas'}</span>}
          {m.overdue > 0 && <span className="pa-k pa-late"><Ic.Clock />{m.overdue} com prazo vencido</span>}
          {m.open > 0 && <span className="pa-k pa-warn"><Ic.Warning />{m.open} sem responsável ou prazo</span>}
        </div>
      )}

      {!c ? <div className="loading-line" /> : (
        <section className="card pa-card">
          <div className="tablewrap nomask">
            <table className="dense pa-table">
              <colgroup>
                <col className="c-pri" /><col className="c-what" /><col className="c-why" /><col className="c-where" /><col className="c-who" />
                <col className="c-when" /><col className="c-how" /><col className="c-much" /><col className="c-x" />
              </colgroup>
              <thead><tr><th>Prioridade</th><th>O quê</th><th>Por quê</th><th>Onde</th><th>Quem</th><th>Quando</th><th>Como</th><th>Quanto custa</th><th aria-label="Excluir" /></tr></thead>
              <tbody>
                {thinking && (
                  <tr className="pa-think"><td colSpan={9}><span className="spin" />A IA está lendo o Strategy — diagnóstico, macro cenário, horizontes — para propor as ações{rows.length ? ' que ainda faltam' : ' iniciais'}…</td></tr>
                )}
                {thinking && !rows.length && [0, 1, 2].map(i => <tr key={i} className="pa-skel"><td colSpan={9}><i /><i /></td></tr>)}
                {rows.map((a, i) => (
                  <Row key={a.id} a={a} first={i > 0 && rows[i - 1]!.priority !== a.priority} fresh={fresh.includes(a.id!)}
                    onPatch={p => patch(a.id!, p)} onRemove={() => remove(a.id!)} onBlur={leave(a.id!)} />
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && !thinking && (
            <div className="pa-empty">
              <p>O plano está vazio. A IA pode propor as ações a partir do que o Strategy registrou, ou comece pela sua.</p>
              <div><button type="button" className="btn-ghost" onClick={() => generate(true)}><Ic.Sparkle />Propor ações com IA</button>
                <button type="button" className="btn-ghost" onClick={add}><Ic.Plus />Adicionar ação</button></div>
            </div>
          )}
          <div className="pa-foot">
            <span>Sempre por prioridade: alta, média, baixa. <Ic.Sparkle className="pa-spark" /> ação proposta pela IA a partir do Strategy — a fonte fica embaixo; edite à vontade. Marque o círculo quando a ação for concluída: o Pulse acompanha a evolução por aí.</span>
            <button type="button" className="addlink" onClick={add}><Ic.Plus />Adicionar ação</button>
          </div>
          {note && <p className="gd-note"><Ic.Warning />{note}</p>}
        </section>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => leaveTo('/strategy')}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => leaveTo('/strategy/horizontes')}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending} onClick={finish}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Concluir ferramenta'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── peças ────────────────────────────────────────────────────────── */

function Row({ a, first, fresh, onPatch, onRemove, onBlur }: {
  a: W5h2Action; first: boolean; fresh: boolean;
  onPatch: (p: Partial<W5h2Action>) => void; onRemove: () => void; onBlur: (e: FocusEvent<HTMLElement>) => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  const txt = (k: 'why' | 'where' | 'who' | 'how', label: string, placeholder: string, max: number) => (
    <td data-label={label}>
      <textarea className="gcell" rows={1} maxLength={max} value={a[k]} placeholder={placeholder} aria-label={`${label} — ${a.what || 'nova ação'}`}
        onChange={e => onPatch({ [k]: e.target.value })} onKeyDown={done} />
    </td>
  );
  return (
    <tr data-ac={a.id} className={`pa-row${first ? ' grp' : ''}${fresh ? ' fresh' : ''}${a.ai ? ' ai' : ''}${a.doneAt ? ' done' : ''}`} onBlur={onBlur}>
      <td className="pa-pri-td" data-label="Prioridade">
        <span className={`pa-pri p-${a.priority}`}>
          <select value={a.priority} aria-label="Prioridade" onChange={e => onPatch({ priority: e.target.value as Prioridade })}>
            {PRIORIDADES.map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
          </select>
          <Ic.Caret />
        </span>
      </td>
      <td className="pa-what-td" data-label="O quê">
        <div className="pa-what-w">
          <button type="button" className="pa-check" aria-pressed={!!a.doneAt} disabled={!a.what.trim()}
            aria-label={a.doneAt ? `Concluída em ${dateBR(a.doneAt)} — reabrir` : 'Marcar como concluída'}
            title={a.doneAt ? `Concluída em ${dateBR(a.doneAt)} — clique para reabrir` : 'Marcar como concluída'}
            onClick={() => onPatch({ doneAt: a.doneAt ? '' : localToday() })}><Ic.Check /></button>
          <div className="pa-what-in">
            {a.ai && <span className="in-ai" title="Proposta pela IA — edite à vontade"><Ic.Sparkle /></span>}
            <textarea className="gcell strong pa-what" rows={1} maxLength={240} value={a.what} placeholder="A ação, começando por um verbo"
              aria-label="O quê" onChange={e => onPatch({ what: e.target.value })} onKeyDown={done} />
            {a.doneAt && <span className="pa-done">Concluída em {dateBR(a.doneAt)}</span>}
            {a.origin && <span className="pa-origin" title="De onde a IA tirou a ação">↳ {a.origin}</span>}
          </div>
        </div>
      </td>
      {txt('why', 'Por quê', 'Por que importa', 240)}
      {txt('where', 'Onde', 'Área, canal ou local', 120)}
      {txt('who', 'Quem', 'Responsável', 120)}
      <td data-label="Quando" className={isOverdue(a, localToday()) ? 'pa-late-td' : undefined}>
        <input type="date" className="gcell gdate pa-date" value={a.when} aria-label="Quando" onChange={e => onPatch({ when: e.target.value })} onKeyDown={done} />
        {isOverdue(a, localToday()) && <span className="pa-late-n">Prazo vencido</span>}
      </td>
      {txt('how', 'Como', 'Como será feito', 240)}
      <td data-label="Quanto custa"><Money value={a.howMuch} onChange={howMuch => onPatch({ howMuch })} /></td>
      <td className="pa-x-td">
        {confirm
          ? <button type="button" className="g-del" onClick={onRemove}>Excluir</button>
          : <button type="button" className="va-btn in-x" onClick={() => setConfirm(true)} aria-label={`Excluir a ação${a.what ? ` ${a.what}` : ''}`}><Ic.Close /></button>}
      </td>
    </tr>
  );
}

function Money({ value, onChange }: { value: number | null; onChange: (n: number | null) => void }) {
  const [edit, setEdit] = useState<string | null>(null);
  return (
    <input className="gcell pa-money" inputMode="decimal" value={edit ?? (value == null ? '' : brl(value))} placeholder="R$" aria-label="Quanto custa"
      onFocus={() => setEdit(value == null ? '' : String(value))} onBlur={() => setEdit(null)} onKeyDown={done}
      onChange={e => { setEdit(e.target.value); onChange(parseMoney(e.target.value)); }} />
  );
}
